import React from "react";
import { Box, Typography, Grid, Paper, Stack } from "@mui/material";

import {
  PuntPlayLog,
  PuntPlayCall,
  PenaltyLog,
} from "../../../../types/LogTypes";
import { jerseyNumberNameDisplay } from "../../../../helpers/helpers";
import { PenaltyLogDetails } from "../Components/PenaltyLogDetails";

interface PuntLogDetailsProps {
  playCall: PuntPlayCall;
  playLog: PuntPlayLog;
  penalties: PenaltyLog[];
}

export const PuntLogDetails: React.FC<PuntLogDetailsProps> = ({
  playCall,
  playLog,
  penalties,
}) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={3}>
        {/* Play Call Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Call
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Personnel
                </Typography>
                <Typography variant="h6">
                  {playCall.personnel_group.name || ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Play
                </Typography>
                <Typography variant="h6">{playCall.name || ""}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Play Result Section */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              height: "100%",
              backgroundColor: "background.paper",
              borderRadius: 2,
              position: "relative",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Play Result
            </Typography>

            {playLog && (
              <Stack spacing={2}>
                {/* Punter */}
                {playLog.punter_roster_player && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Punter
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {jerseyNumberNameDisplay(playLog.punter_roster_player)}
                    </Typography>
                  </Box>
                )}

                {/* Punt Details */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Punt Details
                  </Typography>
                  <Typography variant="body1">
                    From: {playLog.punt_from} yard line
                  </Typography>
                  <Typography variant="body1">
                    To: {playLog.punt_to} yard line
                  </Typography>
                  <Typography variant="h5" color="primary.main" sx={{ mt: 1 }}>
                    {playLog.punt_yardage} yards
                  </Typography>
                </Box>

                {/* Tackle Details */}
                {playLog.tackle?.primary_tackler_roster_player && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tackler
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {jerseyNumberNameDisplay(
                        playLog.tackle.primary_tackler_roster_player
                      )}
                    </Typography>
                  </Box>
                )}

                {/* Fumble Section */}
                {playLog.fumble && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: "error.main",
                      color: "error.contrastText",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      FUMBLE
                    </Typography>
                    {playLog.fumble.fumble_roster_player && (
                      <Typography variant="body2">
                        Fumbled by:{" "}
                        {jerseyNumberNameDisplay(
                          playLog.fumble.fumble_roster_player
                        )}
                      </Typography>
                    )}
                    {playLog.fumble.fumble_recovery_roster_player && (
                      <Typography variant="body2">
                        Recovered by:{" "}
                        {jerseyNumberNameDisplay(
                          playLog.fumble.fumble_recovery_roster_player
                        )}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Penalties Section */}
        <Grid item xs={12} md={4}>
          <PenaltyLogDetails penalties={penalties} />
        </Grid>
      </Grid>
    </Box>
  );
};
